<script setup>

import { computed, onBeforeMount, ref, watch } from 'vue'
import OnboardingService from '@/services/OnboardingService.js'
import { cloneDeep } from 'lodash'
import ApiService from '@/services/ApiService.js'
import AuthService from '@/services/AuthService.js'
import AppService from '@/services/AppService.js'


const emit = defineEmits([
  'finished'
])

const loading = ref(false)
const questions = ref([])
const pages = ref([])
const answers = ref({})
const filteredPages = ref([])
const currentIndex = ref(0)

const pad = (n) => {
  return String(n).padStart(2, '0')
}

const buildStepsIndex = () => {
  let ordinalCounter = 1

  return pages.value.reduce((acc, page) => {
    const pageOrder = page.order
    const pageQuestions = getQuestions(page.key, false)

    // No questions → single slide
    if (!pageQuestions.length) {
      const slide = {
        pageKey: page.key,
        questionKey: null,
        pageOrder,
        questionOrder: null,
        ordinal: pad(ordinalCounter++)
      }

      const key = `${page.key}-${page.key}`
      acc.set(key, slide)

      return acc
    }

    // Page with questions
    pageQuestions.forEach((q, idx) => {
      const slide = {
        pageKey: page.key,
        questionKey: q.key,
        pageOrder,
        questionOrder: idx + 1,
        ordinal: pad(ordinalCounter++)
      }

      const key = `${page.key}-${q.key}`
      acc.set(key, slide)
    })

    return acc
  }, new Map())
}

const getStepId = (pageKey, questionKey = null) => {
  const steps = buildStepsIndex()

  const step = steps.get(`${pageKey}-${questionKey ?? pageKey}`)
  if (!step) return null  // should not happen unless misconfigured

  const { ordinal } = step

  // Construct the final step ID in your desired style
  return `${ordinal}-${questionKey ?? pageKey}`
}

/**
 * Get questions for a specific group. Filter by condition if it exists.
 *
 * @param group
 * @param applyConditions
 * @returns {UnwrapRefSimple<*>[]}
 */
const getQuestions = (group, applyConditions = true) => {
  return questions.value
    .filter(q => q.group === group)
    .filter(q => !applyConditions || !q.condition || q.condition(answers.value))
}

/**
 * Refreshes pages and filter by condition.
 * @returns {Promise<void>}
 */
const refreshPages = async () => {
  const conditions = await Promise.all(
    pages.value.map(page => page.condition ? page.condition() : Promise.resolve(true))
  )

  // add questions
  pages.value.forEach(p => {
    p.questions = getQuestions(p.key)
  })

  // page types that should have questions
  const questionPageTypes = [
    'question-group',
    'promises'
  ]

  const rp = pages.value
    .filter((_, index) => conditions[index])
    // remove pages that have no required questions (e.g. if translations are missing)
    .filter(p => !questionPageTypes.includes(p.type) || p.questions?.length > 0)

  filteredPages.value = rp
}

/**
 * Moves to the next page in the onboarding process.
 * Emit 'finished' event if the last page is reached.
 */
const next = async () => {
  if (!currentPage.value.questions || currentPage.value.questions.length === 0) {
    // pages with questions will be handled by the answer event
    registerAppEvent({ page: currentPage.value.key })
  }

  currentIndex.value++

  if (currentIndex.value >= filteredPages.value.length) {
    // Save the results of the onboarding process
    try {
      await ApiService.saveOnboarding({
        user_id: AuthService.getAuthUser()?.id,
        answers: cloneDeep(answers.value)
      })
    } catch (e) {}
    emit('finished')
  }
}

const registerAppEvent = ({ page, question = null }) => {
  const stepIndex = getStepId(page, question)
  AppService.registerAppEvent('onboarding_step', stepIndex,{
    page: page,
    question: question
  })
}

/**
 * Handles the answer event from child components.
 * Updates the answers object with the provided answer for the given question key.
 * !!!! Also handles child component navigation of sub-pages. answer will be null.
 *
 * @param {Object} payload - The payload containing the answer and question key.
 * @param {any} payload.answer - The answer provided by the user. array in case of multiple choice, string or number in case of single choice.
 * @param {string} payload.question - The key of the question being answered.
 */
const handleAnswer = ({ answer, question }) => {
  registerAppEvent({
    page: currentPage.value.key,
    question
  })

  answers.value[question] = answer
}

/**
 * Computed property to get the current page data based on the current index.
 */
const currentPage = computed(() => {
  if (currentIndex.value < filteredPages.value.length) {
    return filteredPages.value[currentIndex.value]
  }
  return null
})

/**
 * Refresh pages every time answers change.It will reevaluate conditions and update the questions.
 */
watch(() => answers.value, () => refreshPages(), { deep: true })

onBeforeMount(async () => {
  loading.value = true

  const [q, p] = await Promise.all([
    OnboardingService.fetchQuestions(),
    OnboardingService.fetchPages()
  ])

  questions.value = q
  pages.value = p

  await refreshPages()

  loading.value = false
})
</script>

<template>
    <component
            v-if="currentPage"
            :is="currentPage.component"
            :key="currentPage.key"
            :type="currentPage.type"
            :questions="currentPage.questions"
            :all-questions="questions"
            :progress-component="currentPage.progress"
            :answers="answers"
            @answer="handleAnswer"
            @continue="next"
            @finished="emit('finished')"
    />
</template>

<style scoped lang="scss">

</style>
